import { Button, Card } from '@nfp-tracker/components';
import { SUPPORT_MAILTO_URL } from '@nfp-tracker/utils';
import { Lock } from 'lucide-react';
import { ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { trackEvent } from '../integrations/analytics/client';
import { useSubscriptionStorefrontAccess } from '../hooks/useSubscriptionManagementAvailability';
import { useSubscription } from '../hooks/useSubscription';
import { SUBSCRIPTION_STOREFRONT_SUPPORT_MESSAGE } from '../utils/iosExternalBilling';

interface SubscriptionGateProps {
  coupleId: string | undefined;
  children: ReactNode;
  readOnlyMessage?: string;
}

export const SubscriptionGate = ({
  coupleId,
  children,
  readOnlyMessage = 'Your subscription is required to access this feature.',
}: SubscriptionGateProps) => {
  const {
    hasAccess,
    loading: subscriptionLoading,
    status,
    planKey,
    billingProvider,
    daysRemaining,
  } = useSubscription(coupleId);
  const { isSubscriptionManagementAvailable, loading: availabilityLoading } =
    useSubscriptionStorefrontAccess();

  const isBlocked = !subscriptionLoading && !availabilityLoading && !hasAccess;

  useEffect(() => {
    if (!isBlocked) {
      return;
    }

    trackEvent("paywall_viewed", {
      coupleId: coupleId ?? null,
      status,
      planKey,
      billingProvider,
      daysRemaining,
      canSubscribe: isSubscriptionManagementAvailable,
      variant: isSubscriptionManagementAvailable
        ? "subscription_required"
        : "access_unavailable",
      reason: status === "trialing" ? "trial_ended" : "subscription_expired",
    });
  }, [
    isBlocked,
    coupleId,
    status,
    planKey,
    billingProvider,
    daysRemaining,
    isSubscriptionManagementAvailable,
  ]);

  if (subscriptionLoading || availabilityLoading) {
    return (
      <div
        className="flex items-center justify-center rounded-[var(--radius-card-lg)] bg-[var(--color-surface)] p-8 shadow-[var(--shadow-level-1)]"
        role="status"
        aria-label="Loading subscription status"
      >
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!hasAccess) {
    const showSupportFallback = !isSubscriptionManagementAvailable;

    return (
      <Card
        className="mx-auto mt-8 max-w-[480px] rounded-[var(--radius-card-lg)] bg-[var(--color-surface)] p-8 text-center shadow-[var(--shadow-level-1)]"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
            <Lock className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-bold">
            {showSupportFallback ? 'Access unavailable' : 'Subscription Required'}
          </h2>
          <p className="text-muted-foreground">
            {showSupportFallback ? SUBSCRIPTION_STOREFRONT_SUPPORT_MESSAGE : readOnlyMessage}
          </p>
          {!showSupportFallback && (
            <>
              <p className="text-sm text-muted-foreground">
                {status === 'trialing' ? 'Your free trial has ended.' : 'Your subscription has expired.'}
              </p>
              <Link to={ROUTES.subscription}>
                <Button size="lg" className="mt-4">
                  View Subscription Options
                </Button>
              </Link>
            </>
          )}
          {showSupportFallback && (
            <Button asChild size="lg" className="mt-4">
              <a href={SUPPORT_MAILTO_URL}>Contact Support</a>
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return <>{children}</>;
};
